import { getStrings } from './strings';
import { getLocale } from './locale';
import type { GradeResult, PublicMission } from '../types';

/**
 * A checkpoint's verdict, turned into the grade the objectives list draws.
 *
 * A checkpoint is graded away from the tab, against a repository rebuilt from
 * this account's own seed (docs/checkpoints.md), and what comes back is a
 * list of ids rather than a `GradeResult`: every objective and gate that did
 * not hold, and the visible objectives that never ran. This is the one place
 * that turns that list into what "Check my work" shows on every other level,
 * so that pressing it on a checkpoint looks and sounds like pressing it
 * anywhere else.
 *
 * Three kinds of id come back, and each is shown differently:
 *
 *   - **A visible objective.** Drawn in the list like any other — failed,
 *     skipped, or passed. `skipped` is what keeps a skipped objective from
 *     being ticked: without it, "not failed" would read as "passed" for an
 *     objective nobody checked.
 *   - **A gate** (`GATES`). The grader refuses the whole submission before a
 *     single objective runs, so there is nothing to draw in the list and
 *     `grade` is null; the gate says what to fix instead.
 *   - **Anything else** is a hidden objective or the private behaviour check.
 *     The catalogue deliberately ships no titles for those — a list of them is
 *     an inventory of the shortcuts the author thought of — so it is one row,
 *     however many failed, and it never names which.
 */

/**
 * The checkpoints a model has been measured failing alone, and the measurement.
 *
 * Each one says so twice: under its brief, where it also tells the learner to
 * use any tool they like (AI included), and under its row on the public page.
 * Both sentences are claims about a measurement, so they name the model by its
 * exact version and the day it ran — "a model failed this" goes stale with the
 * next release, "this model failed it on this day" never does. A checkpoint
 * joins only when it meets docs/checkpoints.md's standard ("A claim that a
 * model failed"): the reference solution passes, the model fails every blind
 * run alone, the same model passes with the course material, and every
 * failure is audited as the solver's. A checkpoint whose objectives change
 * after its runs leaves the list until it is measured again.
 */
export interface ModelMeasurement {
  /** The model's name as a reader knows it. */
  model: string;
  /** Its exact version, as the API names it. */
  modelId: string;
  /** The UTC day the runs finished, `YYYY-MM-DD`. */
  on: string;
  /** Blind runs, every one of them failed. */
  runs: number;
  /** The grading version the runs were graded on (packages/engine VERIFICATION_VERSION). */
  gradingVersion: string;
}

// Empty since 2026-09-29. cp-whole-job was here from round 4 (Claude Haiku 4.5
// failed it 3 of 3 on pilot-35, 2026-09-27 UTC). Measured again on pilot-38
// under the full standard it passed parts 1, 3 and 4 — Haiku with the course
// passed 2 of 3 — and failed part 2: the first run without the course passed
// (build/blind-course/notes.txt). The trap it had failed was a log on UTC
// beside a pager on the team's clock, which a solver can sidestep by putting
// every row on the log's clock; round 4's three simply had not.
export const MEASURED_AGAINST_A_MODEL: ReadonlyMap<string, ModelMeasurement> = new Map();

/** The grader's gates, in the order it runs them (worker/verification-grade.ts). */
export const GATES = ['repository-integrity', 'clean', 'baseline'] as const;
export type Gate = (typeof GATES)[number];

export interface CheckpointVerdict {
  /** What the objectives list and the verdict line draw; null when a gate refused the work. */
  grade: GradeResult | null;
  /** Gates that refused the work, in the order the grader runs them. */
  gates: Gate[];
  /** The same verdict for the play screen's own bookkeeping, which needs a grade even when nothing was drawn. */
  result: GradeResult;
}

export interface CheckpointAnswer {
  state: 'passed' | 'failed';
  failed?: string[];
  skipped?: string[];
  feedback?: Record<string, { en: string; ar?: string }>;
}

export function checkpointVerdict(mission: PublicMission, answer: CheckpointAnswer, at = Date.now()): CheckpointVerdict {
  const failed = new Set(answer.state === 'passed' ? [] : answer.failed ?? []);
  const skipped = new Set(answer.skipped ?? []);
  const gates = GATES.filter((g) => failed.has(g));
  if (gates.length > 0) {
    return { grade: null, gates, result: { passed: false, objectives: [], gradedAt: at } };
  }
  const visible = new Set(mission.objectives.map((o) => o.id));
  const objectives: GradeResult['objectives'] = mission.objectives.map((o) => ({
    id: o.id,
    title: o.title,
    passed: !failed.has(o.id) && !skipped.has(o.id),
    hidden: false,
    ...(skipped.has(o.id) && !failed.has(o.id) ? { skipped: true } : {}),
    ...(failed.has(o.id) && answer.feedback?.[o.id] ? {
      // The server writes English and Arabic; any other language reads the English.
      message: (getLocale() === 'ar' ? answer.feedback[o.id]!.ar : undefined) ?? answer.feedback[o.id]!.en,
    } : {}),
  }));
  if ([...failed].some((id) => !visible.has(id))) {
    objectives.push({ id: 'hidden', title: getStrings().checkpoint.hiddenFailed, passed: false, hidden: true });
  }
  // The server's word on the whole, not a recount: a pass is a pass even if
  // this build's list of titles has drifted from the one that was graded.
  const grade: GradeResult = { passed: answer.state === 'passed', objectives, gradedAt: at };
  return { grade, gates, result: grade };
}
